import { useState, useCallback } from 'react';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { SCHEMA_STORAGE_KEY } from '../services/SchemaService';
import { OtpRequestChannelType } from '../_types/otp.types';
import { AUTH_TYPES, Schema, SIGNUP_TYPES } from '../_types/schema.types';

interface AuthFlowState {
  isOpenFlow: boolean;
  isOtpAuth: boolean;
  signupType: string;
  authType: string;
  schemaId: string | null;
  otpRequestChannelType: OtpRequestChannelType;
}

export const useSchema = () => {
  console.log('useSchema - Hook initialized');
  
  const [authFlow] = useState<AuthFlowState>(() => {
    const schemaData: Schema | null = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);

    const signupType = schemaData?.signupType || SIGNUP_TYPES.OPEN;
    const authType = schemaData?.authenticationType?.toLowerCase() || AUTH_TYPES.PASSWORD.toLowerCase();
    const otpRequestChannelType = schemaData?.otpRequestChannelType || 'All';

    console.log('useSchema - Initial schemaData:', schemaData);
    console.log('useSchema - Initial authType:', authType);
    console.log('useSchema - Initial otpRequestChannelType:', otpRequestChannelType);

    return {
      isOpenFlow: signupType === SIGNUP_TYPES.OPEN,
      isOtpAuth: authType === AUTH_TYPES.OTP.toLowerCase(),
      signupType,
      authType,
      schemaId: schemaData?.id || null,
      otpRequestChannelType
    };
  });

  // Função para aguardar o carregamento do schema
  const waitForSchema = useCallback(async (maxAttempts: number = 10): Promise<Schema | null> => {
    console.log('useSchema - waitForSchema called');
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const schemaData: Schema | null = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
      
      if (schemaData && schemaData.authenticationType) {
        console.log('waitForSchema - Schema loaded successfully on attempt:', attempt + 1);
        return schemaData;
      }
      
      console.log('waitForSchema - Attempt', attempt + 1, 'of', maxAttempts, '- Schema not ready yet');
      
      // Aguardar 200ms antes da próxima tentativa
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    console.error('waitForSchema - Failed to load schema after', maxAttempts, 'attempts');
    return null;
  }, []);

  const checkIsOpenFlow = useCallback((): boolean => {
    console.log('useSchema - checkIsOpenFlow called, result:', authFlow.isOpenFlow);
    return authFlow.isOpenFlow;
  }, [authFlow.isOpenFlow]);
  
  const checkIsOtpAuth = useCallback((): boolean => {
    console.log('useSchema - checkIsOtpAuth called, result:', authFlow.isOtpAuth);
    return authFlow.isOtpAuth;
  }, [authFlow.isOtpAuth]);
  
  const checkIsPasswordAuth = useCallback((): boolean => {
    const result = authFlow.authType === AUTH_TYPES.PASSWORD.toLowerCase();
    console.log('useSchema - checkIsPasswordAuth called, result:', result);
    return result;
  }, [authFlow.authType]);

  const getAuthenticationType = useCallback(async (): Promise<string> => {
    console.log('useSchema - getAuthenticationType called');
    // Tentar aguardar o carregamento do schema se necessário
    const schemaData = await waitForSchema();
    
    if (schemaData) {
      const authType = schemaData.authenticationType?.toLowerCase() || AUTH_TYPES.PASSWORD.toLowerCase();
      console.log('getAuthenticationType - schemaData:', schemaData);
      console.log('getAuthenticationType - authType:', authType);
      return authType;
    }
    
    // Fallback para leitura direta do localStorage
    const fallbackSchemaData: Schema | null = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    const fallbackAuthType = fallbackSchemaData?.authenticationType?.toLowerCase() || AUTH_TYPES.PASSWORD.toLowerCase();
    
    console.log('getAuthenticationType - fallback authType:', fallbackAuthType);
    return fallbackAuthType;
  }, [waitForSchema]);

  // Versão síncrona para compatibilidade
  const getAuthenticationTypeSync = useCallback(() => {
    console.log('useSchema - getAuthenticationTypeSync called');
    const schemaData: Schema | null = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    const authType = schemaData?.authenticationType?.toLowerCase() || AUTH_TYPES.PASSWORD.toLowerCase();
    
    console.log('getAuthenticationTypeSync - schemaData:', schemaData);
    console.log('getAuthenticationTypeSync - authType:', authType);
    
    return authType;
  }, []);

  const getSchemaId = useCallback(() => {
    console.log('useSchema - getSchemaId called, result:', authFlow.schemaId);
    return authFlow.schemaId;
  }, [authFlow.schemaId]);
  
  const getValidationChannelType = useCallback((): OtpRequestChannelType => {
    console.log('useSchema - getValidationChannelType called, result:', authFlow.otpRequestChannelType);
    return authFlow.otpRequestChannelType;
  }, [authFlow.otpRequestChannelType]);

  // Método para obter o schema completo (útil para outros casos)
  const getSchema = useCallback((): Schema | null => {
    console.log('useSchema - getSchema called');
    return ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
  }, []);

  return {
    ...authFlow,
    checkIsOpenFlow,
    checkIsOtpAuth,
    checkIsPasswordAuth,
    getAuthenticationType,
    getAuthenticationTypeSync,
    getSchemaId,
    getValidationChannelType,
    getSchema,
    waitForSchema
  };
}
 